// import createDebug from 'debug';
import { Area } from 'react-easy-crop/types';
import { Crop } from 'react-image-crop';
import { Resolution } from 'core/types';

// const debug = createDebug('evp:crop-helper');

const MIN_IMAGE_SIZE = {
  width: 640,
  height: 512,
};

function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

function createImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));

    image.src = src;
  });
}

type InvalidSize = 'height' | 'width';

export function cropSizeValid(pixelCrop: Area): [boolean, InvalidSize?] {
  if (pixelCrop.height < MIN_IMAGE_SIZE.height) {
    return [false, 'height'];
  }

  if (pixelCrop.width < MIN_IMAGE_SIZE.width) {
    return [false, 'width'];
  }

  return [true];
}

function dataURItoBlob(dataURI: string): Blob {
  const split = dataURI.split(',');
  const binary = split.length > 1 ? atob(dataURI.split(',')[1]) : atob(dataURI);
  const array = [];
  for (let i = 0; i < binary.length; i += 1) {
    array.push(binary.charCodeAt(i));
  }

  return new Blob([new Uint8Array(array)], { type: 'image/jpeg' });
}

export async function getCroppedImage(
  src: string,
  pixelCrop: Area,
  rotation = 0,
  contentType = 'image/jpeg',
): Promise<Blob> {
  const image = await createImage(src);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create canvas');

  const safeArea = Math.max(image.width, image.height);

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5,
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
    0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y,
  );

  const newImage = canvas.toDataURL(contentType, 0.7);
  return dataURItoBlob(newImage);
}

export async function getCroppedImage2(
  image: HTMLImageElement,
  crop: Crop,
  desiredResolution: Resolution,
  contentType = 'image/jpeg',
): Promise<Blob> {
  // Get the crop as a fraction between 0 and 1.
  // We need this later to translate the crop rectangle from ([0,image.width), [0,image.height))
  // coordinates to ([0,image.naturalWidth), [0,image.naturalHeight)) coordinates
  const cropFracX = crop.x / image.width;
  const cropFracY = crop.y / image.height;
  const cropFracWidth = crop.width / image.width;
  const cropFracHeight = crop.height / image.height;

  // 'crop.width x crop.height' is the aspect ratio we want the final image to be,
  // so find the scale factor which scales that up to our desired resolution
  let canvasScale = desiredResolution[0] / crop.width;
  if (crop.height * canvasScale > desiredResolution[1]) {
    canvasScale = desiredResolution[1] / crop.height;
  }

  // The size of the canvas is the resolution of the image we'll end up with
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(crop.width * canvasScale);
  canvas.height = Math.round(crop.height * canvasScale);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create 2d canvas context');
  }

  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    image.naturalWidth * cropFracX, // source X
    image.naturalHeight * cropFracY, // source Y
    image.naturalWidth * cropFracWidth, // source width
    image.naturalHeight * cropFracHeight, // source height
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise(resolve => {
    canvas.toBlob(
      blob => {
        if (!blob) throw new Error('canvas.toBlob returned null');
        resolve(blob);
      },
      contentType,
      0.8,
    );
  });
}

export async function rotateImage(
  image: HTMLImageElement,
  rotation: number,
  contentType: string,
): Promise<HTMLImageElement> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create canvas');

  const safeArea = Math.max(image.naturalWidth, image.naturalHeight);

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.naturalWidth * 0.5,
    safeArea / 2 - image.naturalHeight * 0.5,
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    0 - safeArea / 2 + image.naturalWidth * 0.5,
    0 - safeArea / 2 + image.naturalHeight * 0.5,
  );

  return createImage(canvas.toDataURL(contentType, 1));
}
